import { Skeleton } from '@/ui/Skeleton';
import { Button } from '@/ui/Button';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { FalloDePanel, Panel, PieDePanel, VacioDePanel } from '@/components/Panel';
import {
  PanelDeCelebraciones, ProximosEventos, TopReconocimientos,
} from '@/components/PanelesDeEquipo';
import { BarraDeProgreso } from '@/components/BarraDeProgreso';
import { FilaEnlace } from './piezas';
import { useAccesos } from '@/lib/accesos';
import {
  cursoParaContinuar, progresoDeInduccion, textoPlano,
  useCursos, useEstadisticas, useFijados, useTramites, useUltimasPublicaciones,
} from './api';

/**
 * Inicio — la primera pantalla tras el acceso, y el destino de rebote de todo.
 *
 * ## Qué es esta pantalla
 *
 * **Lo tuyo, hoy.** Lo que espera algo de ti, lo que celebra tu equipo y desde
 * dónde saltas a hacerlo. No es el muro.
 *
 * Hasta el 2026-09-07 sí lo era, y ese era su problema: Inicio montaba un
 * compositor y un feed que son **los mismos de Novedades**, con los mismos
 * `/posts`, el mismo `Utils.richComposer` y el mismo `PageFeed.renderPostHTML`
 * (los dos borrados el 2026-09-08, al migrar Novedades: el compositor es ahora
 * `features/novedades/piezas.tsx` y la tarjeta `Publicacion.tsx`).
 * Solo que sin adjuntar archivos, sin buscar, sin filtrar por autor, sin rango
 * de fechas y **sin paginar**: pedía quince publicaciones y se acababa. Quien
 * quería leer el muro de verdad tenía que ir a Novedades igual.
 *
 * Ahora Inicio enseña tres titulares y lleva allí. Novedades conserva el muro
 * entero, y con él su tarjeta de publicación — que sigue siendo suya y de
 * Comunidades, no de esta pantalla. Reimplementarla aquí en React habría dejado
 * dos versiones del mismo objeto —reacciones, comentarios, denuncias, adjuntos,
 * galería, métricas— divergiendo a la primera corrección.
 *
 * ## La regla que esta pantalla no cumplía: preguntar por el módulo
 *
 * `dashboard` es `module: null`, así que la alcanza cualquier rol. Pero cuatro
 * de sus diez peticiones están detrás de un `require_module` en el servidor, y
 * ninguna se comprobaba antes de lanzarla. Para un rol sin ese módulo:
 *
 *   · `/posts` devolvía 403 y la pantalla decía «No se pudo cargar el muro» con
 *     un icono de sin-red. Reintentar no lo iba a arreglar nunca.
 *   · El compositor se pintaba entero. La persona escribía, adjuntaba una imagen,
 *     pulsaba Publicar y **entonces** fallaba.
 *   · Los accesos «Reconocer», «Mis cursos» y «Directorio» se pintaban siempre;
 *     al pulsarlos, `navigate()` devolvía a Inicio con un toast de error.
 *
 * El shell ya lo hacía bien: cada entrada del menú lateral y de la barra inferior
 * lleva su `data-module`. Inicio era el único sitio del portal que pintaba
 * contenido de un módulo sin preguntar por él.
 *
 * Aquí se pregunta con `Auth.hasModule`, que es exactamente lo que usa el menú.
 * **No es un control de acceso** —quien autoriza sigue siendo el servidor—: es
 * no ofrecer una puerta que se sabe cerrada.
 *
 * ## Cada bloque carga por su cuenta
 *
 * Ocho peticiones —eran diez—, ninguna bloquea a las demás y cada una enseña su
 * propio esqueleto. Y ninguna se traga su error: la versión vanilla tenía cinco
 * `catch` vacíos, así que un 500 en `/solicitudes/resumen` hacía desaparecer la
 * franja de pendientes sin dejar rastro — indistinguible de «no tienes nada».
 */
export function Inicio() {
  const usuario = Auth.getUser();

  // Los mismos módulos que el menú lateral consulta en su `data-module`.
  const puedeFormacion = Auth.hasModule('cursos');
  const puedeNovedades = Auth.hasModule('feed');
  const puedeKudos = Auth.hasModule('kudos');
  const puedeDirectorio = Auth.hasModule('directorio');
  const puedeEventos = Auth.hasModule('eventos');

  const estadisticas = useEstadisticas();
  const cursos = useCursos(puedeFormacion);
  const tramites = useTramites();
  const publicaciones = useUltimasPublicaciones(puedeNovedades);
  const fijados = useFijados();
  const accesos = useAccesos();

  const induccion = progresoDeInduccion(cursos.data);
  const continuar = cursoParaContinuar(cursos.data);

  const hora = new Date().getHours();
  const saludo = hora < 12 ? 'Buenos días' : hora < 19 ? 'Buenas tardes' : 'Buenas noches';
  const nombre = usuario?.full_name?.split(' ')[0] ?? '';

  const t = tramites.data;
  const enCurso = Math.max(0, (t?.abiertas ?? 0) - (t?.atencion ?? 0));
  const hayTramites = !!t && (t.atencion > 0 || t.por_aprobar > 0 || enCurso > 0);

  // La franja de pendientes solo existe si alguna de sus dos tarjetas tiene algo
  // que decir. Una franja vacía con borde y separación ocupa sitio para no
  // informar de nada — y una tarjeta que siempre marca cero enseña a no mirarla.
  //
  // `isPending` va en la condición y no es un detalle: sin él la tarjeta no se
  // pinta MIENTRAS carga —no hay datos todavía, así que `hayTramites` es
  // falso—, aparece de golpe al llegar la respuesta y empuja hacia abajo todo
  // lo que tiene debajo. Se vio en el navegador el 2026-09-07 midiendo la
  // pantalla a los 300 ms; leyendo el código no se nota, porque en la máquina
  // de quien lo escribe el servidor responde antes de que dé tiempo a verlo.
  const mostrarInduccion = puedeFormacion && (cursos.isPending || induccion !== null || cursos.isError);
  const mostrarTramites = tramites.isPending || hayTramites || tramites.isError;
  const hayFranja = mostrarInduccion || mostrarTramites;

  // Si el rol no tiene ni Novedades ni Formación, la columna principal se queda
  // sin nada y el grid de dos columnas dejaría un hueco de 1fr en blanco.
  const hayColumnaPrincipal = puedeNovedades || puedeFormacion;

  return (
    // `anim-sube-corto`: 240 ms y 6 px. Es lo que se ve justo después de escribir
    // la contraseña, y sin él la pantalla de acceso se cortaba en seco contra
    // esta. Se hace aquí, al montar, y no en `startApp()`: así no hay que tocar
    // el flujo de entrada ni retrasar nada —el armazón sigue mostrándose en el
    // mismo instante— y vale igual al volver a Inicio desde el menú.
    <div className="react-root anim-sube-corto flex flex-col gap-5 pb-10">
      {/* ── Saludo ────────────────────────────────────────────────────── */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-6 font-extrabold tracking-tight text-brand-ink">
            {saludo}
            {nombre && `, ${nombre}`}
          </h1>
          <p className="mt-0.5 text-3 text-ink-2">
            Esto es lo que está pasando hoy en tu equipo
          </p>
          <Contadores
            cargando={estadisticas.isPending}
            datos={estadisticas.data}
            puedeFormacion={puedeFormacion}
            puedeKudos={puedeKudos}
            puedeEventos={puedeEventos}
          />
        </div>

        {/* Accesos rápidos. Cada uno solo aparece si su módulo está en el rol —
            ver la nota de arriba. El de publicar lleva a Novedades, que es donde
            está el compositor: antes enfocaba `home-compose-text`, un id que no
            existe en el repositorio, y el `?.` se tragaba el fallo. */}
        <nav aria-label="Accesos rápidos" className="flex flex-wrap gap-2">
          {puedeNovedades && <AccesoRapido href="#novedades" icono="bi-pencil-square" texto="Publicar algo" />}
          {puedeKudos && <AccesoRapido href="#kudos" icono="bi-star" texto="Reconocer" />}
          {puedeFormacion && <AccesoRapido href="#cursos" icono="bi-play-circle" texto="Mis cursos" />}
          {puedeDirectorio && <AccesoRapido href="#directorio" icono="bi-people" texto="Directorio" />}
        </nav>
      </header>

      {/* ── Fijados ───────────────────────────────────────────────────── */}
      {fijados.data?.map((ev) => (
        <article
          key={ev.id}
          className="rounded-md bg-brand px-5 py-4 text-on-brand"
        >
          {/* La etiqueta iba en `--orange-text` sobre este mismo navy: 1,77 de
              contraste, por debajo de cualquier mínimo. `--orange` sin oscurecer
              da 7,04 — el oscurecido existe para leerse sobre blanco, no aquí.
              Es la tercera vez que este defecto aparece (barra lateral 2026-09-01,
              portada 2026-09-04); lo vigila una prueba. */}
          <p className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-white/12 px-2 py-0.5
                        text-1 font-extrabold uppercase tracking-wider text-accent-on-brand">
            <i className="bi bi-pin-angle-fill" aria-hidden="true" /> Fijado
          </p>
          <h2 className="text-4 font-extrabold">{ev.title}</h2>
          <p className="mt-1 text-2 text-white/75">
            <i className="bi bi-calendar-event" aria-hidden="true" /> {Utils.fmtDatetime(ev.event_date)}
            {/* Va como TEXTO, no interpolado en marcado. `Event.location` es
                texto libre de 200 caracteres que escribe quien crea el evento, y
                la versión vanilla lo metía crudo en el HTML mientras escapaba el
                título de al lado. */}
            {ev.location && (
              <>
                {' · '}
                <i className="bi bi-geo-alt" aria-hidden="true" /> {ev.location}
              </>
            )}
          </p>
          {puedeEventos && (
            <Button
              variant="secondary"
              size="sm"
              // `text-brand` y no `text-brand-ink`, que es la excepción de la
              // regla: al pasar por encima el fondo pasa a blanco LITERAL, que
              // es claro en los dos temas. Con la tinta —que en oscuro se aclara
              // a #7cb3f0— quedaría azul claro sobre blanco.
              className="mt-3 border-white bg-transparent text-on-brand hover:bg-white hover:text-brand"
              // A ESE evento: `#eventos/<id>` existe desde el 2026-09-17. Antes
              // llevaba a la lista, y quien pulsaba tenía que volver a buscarlo.
              onClick={() => APP.navigate(`eventos/${ev.id}`)}
            >
              Ver evento
            </Button>
          )}
        </article>
      ))}

      {/* ── Pendientes ────────────────────────────────────────────────── */}
      {hayFranja && (
        <div className="grid gap-4 sm:grid-cols-2">
          {mostrarInduccion && (
            <Panel icono="bi-mortarboard" titulo="Tu inducción">
              {cursos.isPending ? (
                <Skeleton className="h-16" />
              ) : cursos.isError ? (
                <FalloDePanel que="tu inducción" err={cursos.error} onReintentar={() => void cursos.refetch()} />
              ) : induccion && induccion.pct >= 100 ? (
                <p className="flex items-center gap-2 text-3 font-bold text-ok-ink">
                  <i className="bi bi-patch-check-fill" aria-hidden="true" /> ¡Inducción completada!
                </p>
              ) : induccion ? (
                <>
                  <div className="mb-1.5 flex justify-between text-2 text-muted">
                    <span>{induccion.hechos} de {induccion.total} cursos</span>
                    <strong className="tabular-nums text-warn-ink">{induccion.pct}%</strong>
                  </div>
                  <BarraDeProgreso pct={induccion.pct} etiqueta="Avance de tu inducción" />
                  {induccion.siguiente && (
                    <div className="mt-3">
                      <p className="text-1 uppercase tracking-wide text-muted">Siguiente curso</p>
                      <p className="truncate text-3 font-semibold text-ink">{induccion.siguiente.title}</p>
                      <Button
                        size="sm"
                        block
                        className="mt-2"
                        onClick={() => APP.navigate(`course-detail/${induccion.siguiente!.id}`)}
                      >
                        Continuar <i className="bi bi-arrow-right" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </>
              ) : null}
            </Panel>
          )}

          {mostrarTramites && (
            <Panel icono="bi-file-earmark-text" titulo="Tus trámites">
              {tramites.isPending ? (
                <Skeleton className="h-16" />
              ) : tramites.isError ? (
                <FalloDePanel que="tus trámites" err={tramites.error} onReintentar={() => void tramites.refetch()} />
              ) : (
                <div className="flex flex-col gap-2">
                  {/* Lo que exige acción tuya va primero y destacado; lo demás es
                      informativo. Las sub-rutas están verificadas contra el mapa
                      DE_LA_RUTA de features/solicitudes/Solicitudes.tsx. */}
                  {t!.atencion > 0 && (
                    <FilaEnlace href="#tramites/mis" icono="bi-exclamation-triangle-fill" destacada>
                      <strong>{t!.atencion}</strong>{' '}
                      {t!.atencion === 1
                        ? 'solicitud tuya necesita algo de ti'
                        : 'solicitudes tuyas necesitan algo de ti'}
                    </FilaEnlace>
                  )}
                  {t!.por_aprobar > 0 && (
                    <FilaEnlace href="#tramites/aprobaciones" icono="bi-check2-square" destacada>
                      <strong>{t!.por_aprobar}</strong>{' '}
                      {t!.por_aprobar === 1
                        ? 'solicitud espera tu aprobación'
                        : 'solicitudes esperan tu aprobación'}
                    </FilaEnlace>
                  )}
                  {enCurso > 0 && (
                    <FilaEnlace href="#tramites/mis" icono="bi-hourglass-split">
                      <strong>{enCurso}</strong>{' '}
                      {enCurso === 1 ? 'trámite tuyo en curso' : 'trámites tuyos en curso'}
                    </FilaEnlace>
                  )}
                </div>
              )}
            </Panel>
          )}
        </div>
      )}

      {/* ── Las dos columnas ──────────────────────────────────────────── */}
      <div
        className={cn(
          'grid items-start gap-5',
          hayColumnaPrincipal && 'lg:grid-cols-[minmax(0,1fr)_320px]',
        )}
      >
        {hayColumnaPrincipal && (
          <div className="flex min-w-0 flex-col gap-5">
            {puedeNovedades && (
              <Panel
                icono="bi-chat-square-text"
                titulo="Lo último"
                pie={<PieDePanel href="#novedades">Ver el muro</PieDePanel>}
              >
                {publicaciones.isPending ? (
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-12" />
                    <Skeleton className="h-12" />
                    <Skeleton className="h-12" />
                  </div>
                ) : publicaciones.isError ? (
                  <FalloDePanel
                    que="las publicaciones"
                    err={publicaciones.error}
                    onReintentar={() => void publicaciones.refetch()}
                  />
                ) : !publicaciones.data?.length ? (
                  <VacioDePanel>
                    El muro está tranquilo. Sé el primero en compartir algo con el equipo.
                  </VacioDePanel>
                ) : (
                  <ul className="flex flex-col">
                    {publicaciones.data.map((p) => (
                      <li key={p.id} className="border-b border-line last:border-b-0">
                        <a
                          href="#novedades"
                          className="flex gap-3 rounded-sm px-1 py-2.5 transition-colors hover:bg-sunken
                                     focus-visible:focus-anillo
                                     pointer-coarse:min-h-11"
                        >
                          <Avatar nombre={p.author_name} foto={p.author_avatar} tamano={32} />
                          <div className="min-w-0 flex-1">
                            <p className="flex flex-wrap items-baseline gap-x-2">
                              <span className="truncate text-3 font-semibold text-ink">
                                {p.author_name || 'Usuario'}
                              </span>
                              <span className="text-1 text-muted">{Utils.relativeTime(p.created_at)}</span>
                            </p>
                            {/* Dos líneas y corta. Un titular no es un post: si
                                cupiera entero, esto volvería a ser el muro. */}
                            <p className="line-clamp-2 text-2 text-ink-2">
                              {p.eliminada
                                ? 'Esta publicación fue retirada por su autor.'
                                : textoPlano(p.content)}
                            </p>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            )}

            {puedeFormacion && continuar && (
              <Panel icono="bi-play-circle-fill" titulo="Continúa aprendiendo">
                <p className="mb-2 text-3 font-semibold text-brand-ink">{continuar.title}</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <BarraDeProgreso
                      pct={continuar.progress_pct ?? 0}
                      etiqueta={`Avance de ${continuar.title}`}
                    />
                  </div>
                  <span className="text-2 font-extrabold tabular-nums text-warn-ink">
                    {Math.round(continuar.progress_pct ?? 0)}%
                  </span>
                </div>
                <Button
                  size="sm"
                  block
                  className="mt-3"
                  onClick={() => APP.navigate(`course-detail/${continuar.id}`)}
                >
                  <i className="bi bi-play-fill" aria-hidden="true" /> Continuar
                </Button>
              </Panel>
            )}
          </div>
        )}

        {/* ── Columna de descubrimiento ─────────────────────────────── */}
        <aside className="flex flex-col gap-5 lg:sticky lg:top-20">
          <PanelDeCelebraciones />
          <ProximosEventos />
          <TopReconocimientos habilitado={puedeKudos} />
          {(accesos.favoritos.length > 0 || accesos.recientes.length > 0) && (
            <TusAccesos favoritos={accesos.favoritos} recientes={accesos.recientes} />
          )}
        </aside>
      </div>
    </div>
  );
}

/* ── Contadores del saludo ─────────────────────────────────────────────── */

/**
 * Cursos, kudos y eventos, en una línea.
 *
 * Cada cifra desaparece si su módulo no está en el rol: enseñar «0 Kudos» a
 * quien no tiene reconocimientos no informa de nada, informa mal.
 */
function Contadores({
  cargando, datos, puedeFormacion, puedeKudos, puedeEventos,
}: {
  cargando: boolean;
  datos?: { completed_courses?: number; kudos_received?: number; events_registered?: number };
  puedeFormacion: boolean;
  puedeKudos: boolean;
  puedeEventos: boolean;
}) {
  if (cargando) return <Skeleton className="mt-2 h-4 w-56" />;
  if (!datos) return null;

  const cifras = [
    puedeFormacion && { n: datos.completed_courses ?? 0, etiqueta: 'Cursos' },
    // Recibidos, no enviados: `kudos_given` es 0 permanente para todo
    // colaborador desde que los reconocimientos los otorga solo Bienestar.
    puedeKudos && { n: datos.kudos_received ?? 0, etiqueta: 'Kudos' },
    puedeEventos && { n: datos.events_registered ?? 0, etiqueta: 'Eventos' },
  ].filter(Boolean) as { n: number; etiqueta: string }[];

  if (!cifras.length) return null;

  return (
    <p className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-2 text-muted">
      {cifras.map((c) => (
        <span key={c.etiqueta}>
          <strong className="font-bold tabular-nums text-brand-ink">{c.n}</strong> {c.etiqueta}
        </span>
      ))}
    </p>
  );
}

/* ── Acceso rápido ─────────────────────────────────────────────────────── */

function AccesoRapido({ href, icono, texto }: { href: string; icono: string; texto: string }) {
  return (
    <a
      href={href}
      className="inline-flex items-center gap-2 rounded-sm border border-line bg-sunken px-3 py-2
                 text-3 font-semibold text-ink-2
                 transition-[background-color,color,transform] duration-150
                 hover:-translate-y-px hover:bg-brand hover:text-on-brand
                 motion-reduce:hover:translate-y-0
                 focus-visible:focus-anillo
                 min-h-10 pointer-coarse:min-h-11"
    >
      {/* Sin `color` propio: hereda el del enlace. Antes el icono se pintaba en
          navy y al pasar por encima saltaba a `--orange-text` sobre fondo navy,
          que da 2,52 de contraste. Heredar lo resuelve sin un token nuevo. */}
      <i className={cn('bi', icono)} aria-hidden="true" />
      {texto}
    </a>
  );
}

/**
 * Favoritos y recientes.
 *
 * Solo lectura: fijar y desfijar se hace con la estrella del menú lateral. Ver
 * `accesos.ts` para por qué el shell avisa con un evento y no llamando aquí.
 */
function TusAccesos({
  favoritos, recientes,
}: {
  favoritos: { href: string; label: string; iconClass: string }[];
  recientes: { href: string; label: string; iconClass: string }[];
}) {
  return (
    <Panel icono="bi-star-fill" titulo="Tus accesos">
      {favoritos.length > 0 && (
        <ul className="flex flex-col gap-1">
          {favoritos.map((f) => (
            <li key={f.href}>
              <a
                href={f.href}
                className="flex items-center gap-2.5 rounded-sm bg-sunken px-3 py-2 text-3
                           font-semibold text-ink-2 transition-colors
                           hover:bg-brand hover:text-on-brand
                           focus-visible:focus-anillo
                           pointer-coarse:min-h-11"
              >
                <i className={f.iconClass} aria-hidden="true" />
                <span className="truncate">{f.label}</span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {recientes.length > 0 && (
        <>
          <h3 className="mb-1 mt-3 text-1 font-bold uppercase tracking-wider text-muted">
            Recientes
          </h3>
          {/* Sin «ver más»: la lista completa son diez como mucho (RECENT_MAX en
              app.js), y plegarla a tres obligaba a un botón que solo servía para
              desplegar siete filas. Se muestran las que hay. */}
          <ul className="flex flex-col">
            {recientes.map((r) => (
              <li key={r.href}>
                <a
                  href={r.href}
                  className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-2 text-muted
                             transition-colors hover:bg-sunken hover:text-brand-ink
                             focus-visible:focus-anillo
                             pointer-coarse:min-h-11"
                >
                  <i className={cn(r.iconClass, 'shrink-0')} aria-hidden="true" />
                  <span className="truncate">{r.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}
